/**
 * @architecture-header 1
 * @layer module
 * @domain reports
 * @responsibility Render the marking screen and its per-question drawer.
 * @state-owner dataset store holds answer marks
 * @allowed-imports React; dataset and workspace stores; shared components, selectors and hooks; module comment model
 * @forbidden direct API client; student-only surfaces
 * @public-api workspace main column
 * @side-effects answer mark updates, comment generation and toasts
 * @tests workspace Playwright flows in frontend/apps/web/e2e/workspace.spec.ts
 */

import { Page, PageHeader } from '../../../shared/components/PageShell';
import { type StatusGlyph, StatusIcon } from '../../../shared/components/StatusIcon';
import { CloseIcon } from '../../../shared/components/VispoIcon';
import { tableCells, VTable } from '../../../shared/components/VTable';
import { HINTS, QUESTIONS, WRONG_ANSWERS } from '../../../shared/lib/demo-data';
import type { AnswerMark } from '../../../shared/lib/model';
import { findRun, runName, scoreAnswers } from '../../../shared/lib/selectors';
import { answerKey, useDatasetStore } from '../../../shared/model/dataset-store';
import { useTranslator } from '../../../shared/model/use-workspace';
import { useWorkspaceUiStore } from '../../../shared/model/workspace-store';
import { buildRunComment, resolvePrompt } from '../model/comment';

const MARKS: readonly (readonly [AnswerMark, string])[] = [
  ['ok', '答對'],
  ['no', '答錯'],
  ['fix', '訂正後答對'],
  ['low', '待老師確認'],
];

function markGlyph(mark: AnswerMark): StatusGlyph {
  if (mark === 'ok') return 'done';
  if (mark === 'no') return 'err';
  if (mark === 'fix') return 'fix';
  return 'low';
}

export function AnswerView({ runId, student }: { runId: string; student: string }) {
  const t = useTranslator();
  const classes = useDatasetStore((state) => state.classes);
  const activities = useDatasetStore((state) => state.activities);
  const runs = useDatasetStore((state) => state.runs);
  const sheet = useDatasetStore((state) => state.answers[answerKey(runId, student)]);
  const prompts = useDatasetStore((state) => state.prompts.run);
  const open = useWorkspaceUiStore((state) => state.open);
  const openItem = useWorkspaceUiStore((state) => state.openItem);
  const notify = useWorkspaceUiStore((state) => state.notify);

  const run = findRun(runs, runId);
  if (!run || !sheet) return null;
  const score = scoreAnswers(sheet.marks);
  const questionIndex = open?.q ?? null;

  const confirm = () => {
    const unjudged = sheet.marks.filter((mark) => mark === 'low').length;
    if (unjudged) {
      notify(`${unjudged} ${t('題還沒判定')}`);
      return;
    }
    const store = useDatasetStore.getState();
    store.setAnswerReviewed(runId, student, true);
    store.setReportComment(
      runId,
      student,
      buildRunComment(student, sheet.marks, resolvePrompt('run', prompts), t),
    );
    openItem({ k: 'rev', id: runId, stu: student });
    notify(t('已依作答產生評語'));
  };

  return (
    <Page maxWidth="none">
      <PageHeader
        actions={
          <button className="bt pri" type="button" onClick={confirm}>
            {t('確認並產生評語')}
          </button>
        }
        meta={`${runName(activities, run)} · ${classes[run.classIndex].name} · ${t(run.dateLabel)}`}
        title={student}
      />
      <div className="vtbar">
        <span className="vsumline">
          {t('答對')} <b>{score.correct}</b> {` / ${score.total}`}
          {score.fixed ? (
            <>
              {' '}
              <em>·</em> {t('訂正後答對')} <b>{score.fixed}</b>
            </>
          ) : null}
          {score.unjudged ? (
            <>
              {' '}
              <em>·</em>{' '}
              <span className="wn">
                {t('待確認')} <b>{score.unjudged}</b>
              </span>
            </>
          ) : null}{' '}
          <em>·</em> {t('得分')} <b>{score.percent}</b>
        </span>
        <div className="sp" />
        <span className="vlgd">
          {MARKS.map(([mark, label]) => (
            <span key={mark}>
              <StatusIcon glyph={markGlyph(mark)} />
              {t(label)}
            </span>
          ))}
        </span>
      </div>
      <div className={`vansmain ${questionIndex != null ? 'withq' : ''}`}>
        <div className="vanslist">
          <VTable
            columns={[
              { heading: t('題目') },
              { heading: t('概念') },
              { heading: t('學生作答') },
              { heading: t('狀態') },
            ]}
            rows={QUESTIONS.map((question, index) => ({
              key: question.text,
              on: questionIndex === index,
              onSelect: () => openItem({ k: 'ans', id: runId, stu: student, q: index }),
              cells: tableCells(
                <span className="vnmc">
                  <span className="vnic">{index + 1}</span>
                  <b>{question.text}</b>
                </span>,
                <span className="vmut">{t(question.concept)}</span>,
                <span className="vmut">
                  {sheet.marks[index] === 'ok'
                    ? question.answer
                    : WRONG_ANSWERS[index % WRONG_ANSWERS.length]}
                </span>,
                <span className="vst4">
                  <StatusIcon glyph={markGlyph(sheet.marks[index])} />
                  {t(MARKS.find(([mark]) => mark === sheet.marks[index])?.[1] ?? '')}
                </span>,
              ),
            }))}
            template="minmax(0,1fr) 120px 140px 130px"
          />
        </div>
        {questionIndex != null ? (
          <QuestionPanel index={questionIndex} runId={runId} student={student} />
        ) : null}
      </div>
    </Page>
  );
}

function questionLog(
  mark: AnswerMark,
  index: number,
  t: (value: string) => string,
): readonly (readonly [string, string, string])[] {
  const question = QUESTIONS[index];
  const wrong = WRONG_ANSWERS[index % WRONG_ANSWERS.length];
  const base: readonly (readonly [string, string, string])[] = [
    [`19:0${index + 2}`, 'q', `${t('學生開始作答')}：${question.text}`],
    [`19:0${index + 3}`, 's', `${t('學生寫下')}：${wrong}`],
  ];
  if (mark === 'ok') {
    return [
      [`19:0${index + 2}`, 'q', `${t('學生開始作答')}：${question.text}`],
      [`19:0${index + 3}`, 'ok', `${t('一次就寫對')}：${question.answer}`],
    ];
  }
  if (mark === 'fix') {
    return [
      ...base,
      [`19:0${index + 4}`, 'ai', `${t('AI 提示')}：${t(HINTS[0])}`],
      [`19:0${index + 5}`, 's', `${t('學生改寫')}：${question.answer}`],
      [`19:0${index + 6}`, 'ok', t('訂正後答對')],
    ];
  }
  if (mark === 'low') {
    return [
      ...base,
      [`19:0${index + 4}`, 'ai', `${t('AI 提示')}：${t(HINTS[0])}`],
      [`19:0${index + 5}`, 's', t('學生寫了另一種寫法，AI 無法判定')],
      [`19:0${index + 6}`, 'tea', t('學生自己標記為需要老師確認')],
    ];
  }
  return [
    ...base,
    [`19:0${index + 4}`, 'ai', `${t('AI 提示')}：${t(HINTS[0])}`],
    [`19:0${index + 5}`, 's', t('學生仍然保留同一個錯誤')],
    [`19:0${index + 6}`, 'tea', t('AI 判斷再提示也不會前進，交給老師')],
  ];
}

function QuestionPanel({
  index,
  runId,
  student,
}: {
  index: number;
  runId: string;
  student: string;
}) {
  const t = useTranslator();
  const sheet = useDatasetStore((state) => state.answers[answerKey(runId, student)]);
  const setAnswerMark = useDatasetStore((state) => state.setAnswerMark);
  const openItem = useWorkspaceUiStore((state) => state.openItem);
  if (!sheet) return null;
  const question = QUESTIONS[index];
  const mark = sheet.marks[index];
  const log = questionLog(mark, index, t);

  return (
    <div className="vqp">
      <div className="vqph">
        <b>{`${t('第')} ${index + 1} ${t('題')}`}</b>
        <div className="sp" />
        <button
          className="ib"
          type="button"
          onClick={() => openItem({ k: 'ans', id: runId, stu: student })}
        >
          <CloseIcon />
        </button>
      </div>
      <div className="vqpb">
        <div className="sec5" style={{ marginTop: 0 }}>
          {t('題目')}
        </div>
        <div className="vqtext">{question.text}</div>
        <div className="sec5">{t('學生作答')}</div>
        <div className="work">
          <div className="wline">{WRONG_ANSWERS[index % WRONG_ANSWERS.length]}</div>
          {mark !== 'ok' ? (
            <div className="wline bad">
              {WRONG_ANSWERS[(index + 1) % WRONG_ANSWERS.length]}
              <em>{t(question.concept)}</em>
            </div>
          ) : null}
          {mark === 'fix' ? <div className="wline">{question.answer}</div> : null}
        </div>
        <div className="sec5">{t('正解')}</div>
        <div className="vqtext ok">{question.answer}</div>
        <div className="sec5">{t('判定')}</div>
        <div className="vjrow">
          {MARKS.map(([value, label]) => (
            <button
              className={`vjt ${mark === value ? `on j${value}` : ''}`}
              key={value}
              type="button"
              onClick={() => setAnswerMark(runId, student, index, value)}
            >
              <StatusIcon glyph={markGlyph(value)} />
              {t(label)}
            </button>
          ))}
        </div>
        <div className="sec5">{t('這一題的完整對話')}</div>
        <div className="vtl2">
          {log.map((entry) => (
            <div className={`vtl2r ${entry[1]}`} key={`${entry[0]}-${entry[2]}`}>
              <span className="vtt">{entry[0]}</span>
              <span className="vtxt">{entry[2]}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
